import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EnvironmentService } from '@innovation-os-ng/core/utils';
import { ApiResponse } from '../models/ApiResponse';

export class RestApiException {
  constructor(protected exception: ApiResponse) {}
}
export class AuthenticationInvalidException extends RestApiException {}
export class PasswordResetRequiredException extends RestApiException {}
export class UserNotFoundException extends RestApiException {}
export class InvalidTokenException extends RestApiException {}
export class TokenExpiredException extends RestApiException {}
export class RegistrationExpiredException extends RestApiException {
  get email(): string {
    return this.exception.error.email;
  }
}
export class WeakPasswordException extends RestApiException {}
export class EmailAlreadyTakenException extends RestApiException {}
export class RegistrationNotFoundException extends RestApiException {}
export class LimitExceededException extends RestApiException {}
export class AlreadyUpdatedException extends RestApiException {}

/**
 * Base class, holding shared logic for all REST-API services.
 */
@Injectable({
  providedIn: 'root'
})
export abstract class RestApiService {
  /**
   * Holds the base URL for the REST endpoint.
   */
  protected restUrl: string;

  /**
   * Defines the mapping between error strings and exceptions.
   */
  /* eslint-disable @typescript-eslint/naming-convention */
  protected readonly errorCodes: { [errorCode: string]: typeof RestApiException } = {
    IncorrectUsernameOrPassword: AuthenticationInvalidException,
    PasswordResetRequired: PasswordResetRequiredException,
    UserNotFound: UserNotFoundException,
    InvalidRefreshToken: InvalidTokenException,
    CodeMismatch: InvalidTokenException,
    ExpiredCode: TokenExpiredException,
    InvalidPassword: WeakPasswordException,
    EmailAlreadyExists: EmailAlreadyTakenException,
    SignUpNotFound: RegistrationNotFoundException,
    SignupDoesntExists: RegistrationNotFoundException,
    TokenExpired: RegistrationExpiredException,
    ExceedslimitForRequested: LimitExceededException,
    AlreadyUpdated: AlreadyUpdatedException
  };
  /* eslint-enable @typescript-eslint/naming-convention */

  constructor(readonly environmentService: EnvironmentService, protected readonly httpClient: HttpClient) {
    this.restUrl = environmentService.getRestUrl();
  }

  /**
   * Throws concrete exception classes bases on the error code returned
   * from the API.
   *
   * @throws HttpErrorResponse
   * @throws RestApiException
   * @throws Error
   *
   * @param response
   */
  public convertToException(exception: ApiResponse) {
    // If it's not a proper HttpErrorResponse, throw it
    // again right away.
    if (!(exception instanceof HttpErrorResponse)) {
      throw exception;
    }

    // If the "error.error" does not exist, we did hit a generic
    // network error and can also propagate it.
    if (!exception.error || !exception.error.error) {
      throw exception;
    }

    // Otherwise, try to translate the error code, which was generated
    // by the backend, to a concrete exception class.
    const apiException = this.errorCodes[exception.error.error];
    if (apiException) {
      throw new apiException(exception);
    }

    throw exception;
  }
}
